import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  Button,
  Stack,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import AppModal from "../common/AppModal";
import AppCircularProgress from "../common/AppCircularProgress";
import { useAppDispatch, useAppSelector } from "../../redux/store";

import {
  getAttendanceRecordRoster,
  saveAttendanceRecordBulk,
} from "../../redux/actions/attendanceRecordActions";
import { getTimetableSlots } from "../../redux/actions/timetableSlotActions";

import { AttendanceStatus } from "../../shared/enums/AttendanceStatus";
import { AttendanceRosterResponseDto } from "../../shared/dtos/attendanceRecord/AttendanceRosterResponseDto";
import { AttendanceBulkRequestDto } from "../../shared/dtos/attendanceRecord/AttendanceBulkRequestDto";

const attendanceStatusOptions = Object.values(AttendanceStatus).map(
  (status) => ({
    value: status,
    label: status.replace(/_/g, " "),
  }),
);

interface LocalRosterItem extends AttendanceRosterResponseDto {
  remarks?: string;
}

interface AttendanceRecordModalProps {
  open: boolean;
  onClose: () => void;
  classroomId: string;
  date: string;
}

const AttendanceRecordModal: React.FC<AttendanceRecordModalProps> = ({
  open,
  onClose,
  classroomId,
  date,
}) => {
  const dispatch = useAppDispatch();

  // Redux Selectors
  const { roster, loadingRoster, loadingAttendanceBulk } = useAppSelector(
    (state) => state.attendanceRecord,
  );
  const { timetableSlots, loadingTimetableSlots } = useAppSelector(
    (state) => state.timetableSlot,
  );
  const { userInfo } = useAppSelector((state) => state.auth);

  // Local State
  const [localRoster, setLocalRoster] = useState<LocalRosterItem[]>([]);
  const [selectedSlot, setSelectedSlot] = useState<string>("");

  // 1. Fetch timetable slots on modal open
  useEffect(() => {
    if (open && classroomId) {
      dispatch(getTimetableSlots());
    }
  }, [dispatch, open, classroomId]);

  // 2. Fetch Roster when selected slot or date changes
  useEffect(() => {
    if (open && classroomId) {
      dispatch(
        getAttendanceRecordRoster({
          classroomId,
          date,
          timetableSlotId: selectedSlot || undefined,
        }),
      );
    }
  }, [dispatch, open, classroomId, date, selectedSlot]);

  // 3. Sync Redux roster to local state
  useEffect(() => {
    if (roster) {
      setLocalRoster(
        roster.map((r) => ({
          ...r,
          lastKnownStatus: r.lastKnownStatus || AttendanceStatus.PRESENT,
          remarks: "",
        })),
      );
    }
  }, [roster]);

  // 4. Filter slots for the specific Day of the Week
  const filteredSlots = useMemo(() => {
    if (!timetableSlots) return [];
    const dayName = new Date(date)
      .toLocaleDateString("en-US", { weekday: "long" })
      .toUpperCase();

    return timetableSlots.filter((slot) => slot.dayOfWeek === dayName);
  }, [timetableSlots, date]);

  const handleSave = (): void => {
    const payload: AttendanceBulkRequestDto = {
      classroomId,
      date,
      timetableSlotId: selectedSlot || null,
      recordedByTeacherId: userInfo?.id || "",
      records: localRoster.map((r) => ({
        studentId: r.studentId,
        enrollmentId: r.enrollmentId,
        status: r.lastKnownStatus || AttendanceStatus.PRESENT,
        remarks: r.remarks || null,
      })),
    };
    dispatch(saveAttendanceRecordBulk(payload));
  };

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title="Attendance Entry Grid"
      width="md"
    >
      <Stack spacing={2}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 1.5,
            borderRadius: 1,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="subtitle2">
            Date: <b>{date}</b>
          </Typography>

          <TextField
            select
            label="Lesson / Subject Slot"
            size="small"
            sx={{ minWidth: 280 }}
            value={selectedSlot}
            onChange={(e) => setSelectedSlot(e.target.value)}
            disabled={loadingTimetableSlots}
          >
            <MenuItem value="">
              <em>General Attendance (Daily)</em>
            </MenuItem>
            {filteredSlots.map((slot) => (
              <MenuItem key={slot.id} value={slot.id}>
                {slot.startTime} - {slot.endTime}
              </MenuItem>
            ))}
          </TextField>
        </Box>

        {loadingRoster ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              p: 5,
            }}
          >
            <AppCircularProgress />
            <Typography sx={{ mt: 2, color: "text.secondary" }}>
              Loading Roster...
            </Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Student Name</TableCell>
                  <TableCell sx={{ width: "30%" }}>Status</TableCell>
                  <TableCell sx={{ width: "35%" }}>Remarks</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localRoster.map((row) => (
                  <TableRow key={row.enrollmentId}>
                    <TableCell>
                      <Typography sx={{ fontWeight: 600 }}>
                        {row.studentName}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: "text.secondary" }}
                      >
                        {row.admissionNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.lastKnownStatus || AttendanceStatus.PRESENT}
                        onChange={(e) =>
                          setLocalRoster((prev) =>
                            prev.map((r) =>
                              r.enrollmentId === row.enrollmentId
                                ? {
                                    ...r,
                                    lastKnownStatus: e.target
                                      .value as AttendanceStatus,
                                  }
                                : r,
                            ),
                          )
                        }
                      >
                        {attendanceStatusOptions.map((status) => (
                          <MenuItem key={status.value} value={status.value}>
                            {status.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Optional note"
                        value={row.remarks || ""}
                        onChange={(e) =>
                          setLocalRoster((prev) =>
                            prev.map((r) =>
                              r.enrollmentId === row.enrollmentId
                                ? { ...r, remarks: e.target.value }
                                : r,
                            ),
                          )
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 2,
            pt: 1,
          }}
        >
          <Button onClick={onClose} color="inherit">
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={loadingAttendanceBulk || localRoster.length === 0}
          >
            {loadingAttendanceBulk ? "Saving..." : "Save All"}
          </Button>
        </Box>
      </Stack>
    </AppModal>
  );
};

export default AttendanceRecordModal;
